import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    dialogSubtitle: {
      fontSize: 13,
      fontWeight: 400,
      color: t.textSecondary,
      marginTop: 2,
      letterSpacing: 0,
    },
    intro: { fontSize: 13, lineHeight: 1.5, color: t.textSecondary, margin: "0 0 16px" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    result: {
      marginTop: 8,
      padding: "12px 14px",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      backgroundColor: t.muted,
      fontSize: 13,
      lineHeight: 1.5,
      color: t.text,
    },
    resultMessage: {
      margin: "8px 0 0",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontFamily: "inherit",
      fontSize: 13.5,
    },
    resultWarnings: {
      marginTop: 8,
      padding: "6px 10px",
      borderRadius: t.radius.badge,
      backgroundColor: t.status.warn.bg,
      color: t.status.warn.fg,
      fontSize: 12,
    },
    resultFollowUp: { marginTop: 8, fontSize: 12, color: t.textMuted },
  };
});

// Sends the rule's first invoice of the day to a single phone number (campaign "[TESTE] …"),
// through the same worker used by the real run (text, copy buttons, PDF / official template).
const BillingRuleTestSendDialog = ({ open, rule, onClose }) => {
  const classes = useStyles();
  const [number, setNumber] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setResult(null);
      setSending(false);
    }
  }, [open, rule]);

  const digits = number.replace(/\D/g, "");
  const valid = digits.length >= 10 && digits.length <= 13;

  const handleSend = async () => {
    if (!rule || !valid) return;
    setSending(true);
    try {
      const { data } = await api.post(`/billing-rules/${rule.id}/test-send`, { number: digits });
      if (!isMountedRef.current) return;
      setResult(data);
      toast.success(i18n.t("billingRules.toasts.testSent", { number: data.contactNumber }));
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>
        {i18n.t("billingRules.testSend.title")}
        {rule && <div className={classes.dialogSubtitle}>{rule.name}</div>}
      </DialogTitle>
      <DialogContent>
        <p className={classes.intro}>{i18n.t("billingRules.testSend.help")}</p>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="billing-rule-test-number">
            {i18n.t("billingRules.testSend.number")}
          </label>
          <TextField
            id="billing-rule-test-number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="51999998888"
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            disabled={sending}
            error={number.length > 0 && !valid}
            helperText={number.length > 0 && !valid ? i18n.t("billingRules.testSend.numberInvalid") : " "}
          />
        </div>
        {result && (
          <div className={classes.result}>
            <div>
              <strong>{i18n.t("billingRules.testSend.sentTo")}:</strong> {result.contactNumber}
            </div>
            <div>
              <strong>{i18n.t("billingRules.testSend.invoiceOf")}:</strong> {result.customerName || "-"}
            </div>
            {Array.isArray(result.buttons) && result.buttons.length > 0 && (
              <div>🔘 {result.buttons.join(" · ")}</div>
            )}
            <pre className={classes.resultMessage}>{result.renderedMessage}</pre>
            {Array.isArray(result.warnings) && result.warnings.length > 0 && (
              <div className={classes.resultWarnings}>{result.warnings.join(" · ")}</div>
            )}
            <div className={classes.resultFollowUp}>
              {i18n.t("billingRules.testSend.followUp", { id: result.invoiceCampaignId })}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {i18n.t("billingRules.buttons.cancel")}
        </Button>
        <Button onClick={handleSend} color="primary" variant="contained" disabled={sending || !valid}>
          {i18n.t("billingRules.testSend.send")}
          {sending && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillingRuleTestSendDialog;
